import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../form-layout";
import axios from "axios";
import { computeFinalTier } from "../../lib/eligibility";

// Q22 — Acknowledgement statement. Final screen before submission: computes the
// overall tier (Green/Amber/Orange/Red) as a safety-net double-check and attaches it
// to the payload sent to the backend for doctor review. Per the doc, "[Next]" here is
// immediately followed by "Please Register as a New Patient" — so a successful save
// sends the patient to account creation (/auth/signup), not the in-flow
// contact-details/checkout steps.
export default function Acknowledgement({ formData, setFormData, onBack, goToStep }) {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(formData.acknowledgementAgreed || false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;

    const { tier, flags } = computeFinalTier(formData);

    const allData = { ...formData, acknowledgementAgreed: true, eligibilityFlags: { tier, flags } };
    Object.keys(localStorage).forEach((key) => {
      try {
        allData[key] = JSON.parse(localStorage.getItem(key));
      } catch {
        allData[key] = localStorage.getItem(key);
      }
    });
    allData.eligibilityFlags = { tier, flags };

    // Save locally first — this is the source of truth the rest of the flow (and a
    // page refresh) relies on, so it must succeed before anything else.
    setFormData((prev) => ({ ...prev, acknowledgementAgreed: true }));
    localStorage.setItem("acknowledgementAgreed", "true");
    localStorage.setItem("submittedAssessment", JSON.stringify(allData));

    setLoading(true);

    // Sync to the backend. A failure here must never block the patient — the
    // submission already lives in localStorage regardless of network outcome — but we
    // do wait for it (briefly) so we can carry the returned assessment_id forward to
    // signup, which is what lets the backend link this assessment to the account the
    // patient is about to create.
    let assessmentId = null;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (API_BASE_URL) {
      try {
        const response = await axios.post(`${API_BASE_URL}/form/save`, allData, {
          headers: { "Content-Type": "application/json" },
          timeout: 5000,
        });
        assessmentId = response.data?.assessment_id ?? null;
      } catch (err) {
        console.warn("Save to backend failed (saved locally instead):", err);
      }
    }

    setLoading(false);
    setSuccess(true);

    setTimeout(() => {
      // Questionnaire is complete — clear all saved progress so the next visit starts
      // a fresh assessment instead of resuming this one. allData/assessmentId above
      // already captured everything needed before this runs.
      localStorage.clear();
      navigate("/auth/signup", { state: { assessmentId } });
    }, 900);
  };

  return (
    <Layout goToStep={goToStep} onBack={onBack}>
      <h2 className="text-base font-semibold text-center mb-4">Acknowledgement Statement</h2>
      <p className="text-sm text-gray-700 mb-4">By continuing, I confirm that:</p>

      <div className="text-sm text-gray-700 space-y-3 mb-6 max-h-64 overflow-y-auto pr-1">
        <p className="font-medium">Consent and treatment</p>
        <ul className="list-disc list-inside space-y-1">
          <li>My information is accurate and complete. I will report any changes to my health or medication.</li>
          <li>I give Slimming Doctor permission to share relevant treatment information with my GP.</li>
          <li>I understand my information will be handled confidentially under UK data-protection laws.</li>
          <li>
            I will read the medication leaflet, follow my prescribed dose and contact the clinical team
            before restarting after a break of more than four weeks.
          </li>
          <li>I understand that submitting an application does not guarantee a prescription.</li>
        </ul>

        <p className="font-medium">Safety</p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            I will not use this medication during pregnancy. I will follow the contraception and
            pregnancy-planning advice for my medication.
          </li>
          <li>I will contact the clinical team if I become pregnant, suspect pregnancy or plan to conceive.</li>
          <li>
            I understand that nausea, vomiting, diarrhoea, constipation and abdominal discomfort can occur.
            I will stay hydrated and seek advice if symptoms are severe, persistent or prevent me from
            keeping fluids down.
          </li>
          <li>
            I will stop treatment and seek urgent medical advice for severe or persistent abdominal pain,
            especially if it spreads to my back.
          </li>
          <li>
            I will seek urgent help for neck swelling, swallowing or breathing difficulties, or serious
            changes in my mental health. In an emergency, I will call 999.
          </li>
          <li>
            If I take oral progesterone as part of HRT, I will discuss this with my prescriber, as
            treatment may affect its absorption.
          </li>
        </ul>

        <p className="font-medium">Verification — no uploads needed now</p>
        <p>After I submit my application, Slimming Doctor will send a secure link by text or email to request:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Identity: photo ID and, if needed, a recent photograph of myself.</li>
          <li>BMI: verification of my current weight.</li>
          <li>
            Current GLP-1 treatment, if applicable: evidence of my medication and dose, such as a labelled
            pen box, NHS App record, prescription or pharmacy invoice.
          </li>
        </ul>
        <p>
          Evidence must be clear, recent and match my application. Further information may be needed.
          Treatment cannot proceed until the required checks are complete. If I do not provide the required
          verification and my application cannot proceed, a £20 administration charge will be deducted
          from any refund issued.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <label className="flex items-start gap-2 p-3 rounded-lg border cursor-pointer text-sm mb-4">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="h-4 w-4 accent-sky-400 cursor-pointer mt-0.5"
          />
          <span>
            I have read and understood the above, had the opportunity to ask questions, and agree to
            provide the required verification.
          </span>
        </label>

        {success && (
          <p className="text-green-600 text-sm font-medium mb-3">
            Your answers have been saved. Continuing…
          </p>
        )}

        <div className="flex justify-start">
          <button
            type="submit"
            disabled={!agreed || loading || success}
            className={`w-[210px] text-sm font-semibold rounded-lg py-3 transition-colors
              ${
                !agreed || loading || success
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "bg-[#35C3D9] text-white hover:bg-[#27AAC0]"
              }`}
          >
            {success ? "Saved ✓" : loading ? "Saving..." : "Next →"}
          </button>
        </div>
      </form>
    </Layout>
  );
}
